import { useRouter } from '@tanstack/react-router';
import { useRegisterBreadcrumbs } from '@/services/breadcrumbs';
import { generatePath } from '@/services/router/generate-path';

/**
 * The Integrations crumb points at the `journeys` URL deliberately: it is correct whether or not
 * `hydra-integrations-directory` is on. Flag off, Ember serves it; flag on, Hydra claims it and
 * forwards to the migrated directory.
 */
const EMBER_INTEGRATIONS_PATH = '/workspaces/:workspaceId/journeys/integrations';

/** Where Cancel goes, matching Ember's `urlFor 'integrations.index'`. */
export function useIntegrationsIndexHref(workspaceId: string): string {
  return generatePath(EMBER_INTEGRATIONS_PATH, { workspaceId });
}

/**
 * Where a successful save returns to — Ember's `integrations.all.overview`, the connections tab of
 * the directory. Also routed through the `journeys` URL so it resolves in both flag states.
 */
export function useIntegrationsOverviewHref(workspaceId: string): string {
  return generatePath('/workspaces/:workspaceId/journeys/integrations/all/overview', { workspaceId });
}

/**
 * The connector's breadcrumb label, derived from the URL segment rather than the catalog name:
 * the crumb reads "Mixpanel" where the page title reads "Mixpanel (Message Activity)".
 */
export function dataOutCrumbLabel(type: string): string {
  return type
    .replace(/_/g, ' ')
    .replace(/Out$/, '')
    .replace(/^./, (first) => first.toUpperCase());
}

export function useDataOutBreadcrumbs(workspaceId: string, type: string): void {
  const router = useRouter();

  useRegisterBreadcrumbs([
    {
      id: 'integrations',
      text: 'Integrations',
      href: generatePath(EMBER_INTEGRATIONS_PATH, { workspaceId }),
    },
    {
      id: 'integrations_data_out',
      text: dataOutCrumbLabel(type),
      href: router.buildLocation({
        to: '/workspaces/$workspaceId/integrations/data_out/$type',
        params: { workspaceId, type },
      }).href,
      isTruncated: true,
    },
  ]);
}
